import * as React from 'react'
import { useFormikContext } from 'formik'

import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import Stack from '@mui/material/Stack'

import { AdditionalLeadField } from '@user/features/PendingLeadAutoReview/fields/AdditionalLeadField'
import { AutoReviewCustomField } from '@user/features/PendingLeadAutoReview/fields/AutoReviewCustomField'
import type { MissingFieldGroup } from '@user/features/PendingLeadAutoReview/typings'

import { LeadCompanyRequiredField } from './LeadCompanyRequiredField'
import type { UseData } from './hooks/useData'
import type { LeadCompanyRequiredFieldsFormValues } from './hooks/useInitialValues'

type Props = {
  data: UseData
  onClose: () => void
}

function renderGroup(group: MissingFieldGroup) {
  switch (group.kind) {
    case 'additional':
      return <AdditionalLeadField key={group.key} fieldKey={group.key} />
    case 'custom':
      return (
        <AutoReviewCustomField
          key={`custom-${group.fieldId}`}
          fieldId={group.fieldId}
        />
      )
    default:
      return null
  }
}

/** Fields and Cancel/Save footer of the lead "Update Required Fields" modal. */
export function LeadCompanyRequiredFieldsForm(props: Props) {
  const { data, onClose } = props
  const { loading, groups, extraKeys } = data

  const { isSubmitting, submitForm } =
    useFormikContext<LeadCompanyRequiredFieldsFormValues>()

  return (
    <>
      <DialogContent sx={{ px: '24px', pt: 0 }}>
        {loading ? (
          <CircularProgress />
        ) : (
          <Stack spacing={2} sx={{ pt: 2 }}>
            {groups.map(renderGroup)}
            {extraKeys.map((key) => (
              <LeadCompanyRequiredField key={key} fieldKey={key} />
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          borderTop: '1px solid #e2e5ea',
          px: '24px',
          py: '16px',
        }}
      >
        <Button
          size="large"
          onClick={onClose}
          data-test-id="lead-company-required-fields-cancel"
        >
          Cancel
        </Button>
        <Button
          size="large"
          variant="contained"
          disabled={isSubmitting}
          onClick={submitForm}
          data-test-id="lead-company-required-fields-submit"
        >
          Save
        </Button>
      </DialogActions>
    </>
  )
}

LeadCompanyRequiredFieldsForm.displayName = 'LeadCompanyRequiredFieldsForm'
